import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BRAND from '../../config/brand';
import {
  HomeOutlined,
  AppstoreOutlined,
  TeamOutlined,
  LoginOutlined,
  UserAddOutlined,
  DownOutlined,
  RightOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  DashboardOutlined,
  UserOutlined,
  FileTextOutlined,
  SafetyOutlined,
  PlusOutlined,
  TagOutlined,
} from '@ant-design/icons';
import './Sidebar.css';

const menuItems = [
  {
    key: '/',
    icon: <HomeOutlined />,
    label: '首页',
    path: '/',
  },
  {
    key: '/skills',
    icon: <AppstoreOutlined />,
    label: '技能商店',
    path: '/skills',
  },
  {
    key: '/experts',
    icon: <TeamOutlined />,
    label: '音乐行业专家',
    path: '/experts',
  },
  {
    key: '/publish',
    icon: <PlusOutlined />,
    label: '发布技能',
    path: '/publish',
  },
  {
    type: 'divider',
  },
  {
    key: '/login',
    icon: <LoginOutlined />,
    label: '登录',
    path: '/login',
  },
  {
    key: '/register',
    icon: <UserAddOutlined />,
    label: '注册',
    path: '/register',
  },
  {
    type: 'divider',
  },
  {
    key: 'admin',
    icon: <SettingOutlined />,
    label: '管理后台',
    path: '/admin',
    children: [
      { key: '/admin/dashboard', icon: <DashboardOutlined />, label: '数据概览', path: '/admin/dashboard' },
      { key: '/admin/skills', icon: <AppstoreOutlined />, label: '技能管理', path: '/admin/skills' },
      { key: '/admin/experts', icon: <TeamOutlined />, label: '专家管理', path: '/admin/experts' },
      { key: '/admin/categories', icon: <TagOutlined />, label: '分类管理', path: '/admin/categories' },
      { key: '/admin/users', icon: <UserOutlined />, label: '用户管理', path: '/admin/users' },
      { key: '/admin/orders', icon: <FileTextOutlined />, label: '订单管理', path: '/admin/orders' },
      { key: '/admin/audit', icon: <SafetyOutlined />, label: '审核中心', path: '/admin/audit' },
    ],
  },
];

const Sidebar = ({ collapsed, onCollapse, mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [expandedKeys, setExpandedKeys] = useState(['skills']);
  const [localUser, setLocalUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (saved && token) {
      try {
        setLocalUser(JSON.parse(saved));
      } catch (e) {
        setLocalUser(null);
      }
    }
    setAuthChecked(true);
  }, [isAuthenticated, user]);

  const effectiveAuth = isAuthenticated || (!!localUser && !!localStorage.getItem('token'));

  const toggleExpand = (key) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const renderMenuItem = (item) => {
    if (item.type === 'divider') {
      return <div key="divider" className="sidebar-divider" />;
    }

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedKeys.includes(item.key);
    const active = isActive(item.path);

    return (
      <div key={item.key} className="menu-item-wrapper">
        <Link
          to={item.path}
          className={`menu-item ${active ? 'active' : ''} ${hasChildren ? 'has-children' : ''}`}
          onClick={(e) => {
            if (hasChildren) {
              e.preventDefault();
              toggleExpand(item.key);
            } else if (mobileOpen && onMobileClose) {
              // 移动端点击普通菜单项后关闭抽屉
              onMobileClose();
            }
          }}
        >
          <span className="menu-icon">{item.icon}</span>
          {!collapsed && <span className="menu-label">{item.label}</span>}
          {!collapsed && hasChildren && (
            <span className="menu-arrow">
              {isExpanded ? <DownOutlined /> : <RightOutlined />}
            </span>
          )}
        </Link>
        {!collapsed && hasChildren && isExpanded && (
          <div className="submenu">
            {item.children.map((child) => (
              <Link
                key={child.key}
                to={child.path}
                onClick={() => {
                  if (mobileOpen && onMobileClose) onMobileClose();
                }}
                className={`submenu-item ${isActive(child.path) ? 'active' : ''}`}
              >
                <span className="submenu-icon">{child.icon}</span>
                <span className="submenu-label">{child.label}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  // 根据登录状态过滤菜单
  const getFilteredMenuItems = () => {
    // 检查是否是管理员
    const currentUser = user || localUser;
    const isAdmin = currentUser?.role === 'admin';
    
    return menuItems.filter(item => {
      // 登录后隐藏登录/注册
      if (effectiveAuth && (item.key === '/login' || item.key === '/register')) {
        return false;
      }
      // 未登录时隐藏管理后台和发布技能
      if (!effectiveAuth && (item.key === 'admin' || item.key === '/publish')) {
        return false;
      }
      // 非管理员隐藏管理后台
      if (effectiveAuth && !isAdmin && item.key === 'admin') {
        return false;
      }
      return true;
    });
  };

  // 点击侧边栏外部关闭移动端菜单
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && onMobileClose) {
      onMobileClose();
    }
  };

  return (
    <>
      {/* 移动端遮罩层 */}
      {mobileOpen && (
        <div 
          className="sidebar-overlay" 
          onClick={handleOverlayClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 99,
          }}
        />
      )}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <span className="logo-icon">🎵</span>
            {!collapsed && <span className="logo-text">{BRAND.name}</span>}
          </div>
          <button className="collapse-btn" onClick={onCollapse}>
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>
        </div>
        <nav className="sidebar-menu">
          {getFilteredMenuItems().map(renderMenuItem)}
          
          {/* 登录后显示用户信息 */}
          {effectiveAuth && !collapsed && (
            <div className="sidebar-user-info">
              <div className="user-divider" />
              <div className="user-profile">
                <span className="user-avatar-small">👤</span>
                <span className="user-name-small">{(user || localUser)?.name || (user || localUser)?.email}</span>
              </div>
            </div>
          )}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
